import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useRef } from "react";

import { $gitStatus } from "../../internal/git";

/**
 * Hook to access git repository status
 * @param documentId - The document ID
 * @returns The git status or undefined if not available
 */
export const useGitStatus = (documentId: string | undefined) => {
	const statusAtom = $gitStatus(documentId);
	const statusLoadable = useAtomValue(loadable(statusAtom));
	const refresh = useSetAtom(statusAtom);

	// Keep track of the last successful data to avoid flickering while loading
	const lastDataRef = useRef(
		statusLoadable.state === "hasData" ? statusLoadable.data : undefined
	);
	const prevDocumentIdRef = useRef(documentId);

	// Reset stale data when the document changes so that a previous document's
	// conflict state is never attributed to the newly-navigated document.
	if (prevDocumentIdRef.current !== documentId) {
		prevDocumentIdRef.current = documentId;
		lastDataRef.current = statusLoadable.state === "hasData" ? statusLoadable.data : undefined;
	}

	// Update the ref when we have new data
	if (statusLoadable.state === "hasData") {
		lastDataRef.current = statusLoadable.data;
	}

	return {
		// Return last successful data while loading (stale-while-revalidate)
		data: statusLoadable.state === "hasData" ? statusLoadable.data : lastDataRef.current,
		refresh,
		isLoading: statusLoadable.state === "loading",
		isError: statusLoadable.state === "hasError",
	};
};
